import { useCallback, useRef, useState } from 'react';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';

/**
 * Camera barcode scanning with graceful fallback. Uses the native BarcodeDetector API where
 * present (Chrome/Edge/Android) and falls back to zxing-js (iOS Safari). Typing always stays
 * available in ScanInput, so this hook never blocks the bench flow. Net-new for Lab Intake.
 */

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (opts?: { formats?: string[] }) => BarcodeDetectorLike;
declare global {
  // eslint-disable-next-line no-var
  interface Window {
    BarcodeDetector?: BarcodeDetectorCtor;
  }
}

const FORMATS = ['code_128', 'code_39', 'ean_13', 'itf', 'qr_code'];

export function useBarcodeScanner() {
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<number | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stop = useCallback(() => {
    if (intervalRef.current != null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (controlsRef.current) {
      controlsRef.current.stop();
      controlsRef.current = null;
    }
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
      streamRef.current = null;
    }
    setScanning(false);
  }, []);

  const start = useCallback(
    async (video: HTMLVideoElement, onResult: (text: string) => void) => {
      setError(null);
      const Detector = window.BarcodeDetector;
      try {
        if (Detector) {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
          streamRef.current = stream;
          video.srcObject = stream;
          await video.play();
          const detector = new Detector({ formats: FORMATS });
          setScanning(true);
          intervalRef.current = window.setInterval(() => {
            void detector
              .detect(video)
              .then((codes) => {
                const hit = codes.find((c) => c.rawValue && c.rawValue.trim());
                if (hit) {
                  onResult(hit.rawValue.trim());
                  stop();
                }
              })
              .catch(() => {
                /* transient per-frame decode error */
              });
          }, 300);
        } else {
          const reader = new BrowserMultiFormatReader();
          setScanning(true);
          controlsRef.current = await reader.decodeFromVideoDevice(undefined, video, (result) => {
            if (result) {
              onResult(result.getText().trim());
              stop();
            }
          });
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Camera unavailable');
        stop();
      }
    },
    [stop],
  );

  const supported =
    typeof navigator !== 'undefined' && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';
  return { supported, scanning, error, start, stop };
}
